import { trpc } from "../trpc";
import {
  FolderTreeItem,
  insertFolderIntoTree,
  replaceFolderInTree,
} from "../folderTree";
import type { FolderTreeOutput } from "@zapier/bff";
import { useCurrentAccountId } from "@zapier/identity";
import { TEMP_FOLDER_ID } from "@zapier/utils";

export function useCreateSubfolder() {
  const utils = trpc.useUtils();
  const accountId = useCurrentAccountId();

  const mutation = trpc.folder.createSubfolder.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ name, isShared, parentId }) => {
      utils.folder.folderTree.cancel(); // cancel outgoing refetches
      // Update Folder under folderTree key
      utils.folder.folderTree.setData(
        {},
        (old: FolderTreeOutput | undefined) => {
          if (old?.hash) {
            const newSubfolder = {
              accountId: Number(accountId) || 0,
              children: [],
              id: TEMP_FOLDER_ID,
              isShared,
              name,
              parentId: parentId,
              rootId: parentId,
              zapCount: 0,
            };

            const updatedFolderTree = insertFolderIntoTree(
              old.tree,
              newSubfolder,
            );

            // this needs to return a new object ref for RQ to detect the change
            return {
              ...old,
              // update hash
              hash: {
                ...old.hash,
                [TEMP_FOLDER_ID]: newSubfolder,
              },
              // update tree
              tree: updatedFolderTree as FolderTreeItem[],
            };
          }

          return old;
        },
      );
    },
    // Replace the temporary optimistic subfolder with the real server data
    // so the cache has the real folder ID before component-level onSuccess
    // callbacks run (e.g. navigation to the new folder URL).
    onSuccess: (data) => {
      // The first element in the response array is the newly created folder
      const createdFolder = data[0];
      if (!createdFolder) {
        return;
      }

      utils.folder.folderTree.setData(
        {},
        (old: FolderTreeOutput | undefined) => {
          if (!old?.hash) {
            return old;
          }

          const realFolder: FolderTreeItem = {
            id: createdFolder.id,
            name: createdFolder.name,
            isShared: createdFolder.isShared,
            accountId: createdFolder.accountId,
            ownerId: createdFolder.ownerId,
            parentId: createdFolder.parentId,
            rootId: createdFolder.rootId,
            children: [],
          };

          const updatedTree = replaceFolderInTree(
            old.tree,
            TEMP_FOLDER_ID,
            realFolder,
          );

          // Remove the temp entry from hash and add the real one
          const { [TEMP_FOLDER_ID]: _removed, ...remainingHash } = old.hash;

          return {
            ...old,
            tree: updatedTree as FolderTreeItem[],
            hash: {
              ...remainingHash,
              [createdFolder.id]: realFolder,
            },
          };
        },
      );
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      // Invalidate folderTree key to refetch data
      utils.folder.folderTree.invalidate();
    },
  });

  return {
    createSubfolder: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
